import React, { useContext, useMemo } from 'react';
import { type AuthAction } from '../state/authenticationActions';
import { type AuthenticationState } from '../state/authenticationReducer';
import { type LogoutHandler } from '../types';

const AuthenticationContext = React.createContext<
  | {
      logoutHandlers: { current: LogoutHandler }[];
      authState: AuthenticationState;
      authDispatch: React.Dispatch<AuthAction>;
    }
  | undefined
>(undefined);

/**
 * Provider for authentication context.
 *
 * You shouldn't need to use this outside of core/authentication.  Use
 * AuthenticationProvider instead.
 */
export const AuthenticationContextProvider: React.FC<{
  authState: AuthenticationState;
  authDispatch: React.Dispatch<AuthAction>;
  logoutHandlers: { current: LogoutHandler }[];
  children: React.ReactNode;
}> = ({ authState, authDispatch, logoutHandlers, children }) => {
  const value = useMemo(
    () => ({ logoutHandlers, authState, authDispatch }),
    [logoutHandlers, authState, authDispatch],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

/**
 * Provider for authentication context for public routes.
 *
 * Designed for public routes that do not require authentication, this provider creates an empty user session with no actions.
 * It ensures that consuming the user session does not throw an error for e.g. (Missing AuthenticationProvider).
 * This approach explicitly wraps public routes with a context that does not include a user session, eliminating the need to propagate the user session throughout the application.
 */
export const PublicAuthenticationContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const value = useMemo(
    () => ({
      logoutHandlers: [],
      authState: { loading: false, isPublicSession: true },
      authDispatch: () => null,
    }),
    [],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

/**
 * Returns the current authentication state.  You shouldn't need to call this directly
 * outside of core/authentication.
 */
export function useAuthenticationState(): [AuthenticationState, React.Dispatch<AuthAction>] {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(`Missing AuthenticationProvider`);
  }

  return [context.authState, context.authDispatch];
}

export function useLogoutHandlers(): { current: LogoutHandler }[] {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error(`Missing AuthenticationProvider`);
  }

  return context.logoutHandlers;
}
